/**
 * What a new password has to satisfy before it is sent.
 *
 * The server accepts any non-empty password, so this is the only place a floor is enforced. Hosts
 * that need a stricter rule pass their own policy rather than editing the default.
 */
export type PasswordPolicy = {
  /** Fewest characters a new password may have. */
  minLength: number;
  requireMixedCase?: boolean;
  requireNumber?: boolean;
  requireSymbol?: boolean;
};

export const defaultPasswordPolicy: PasswordPolicy = {
  minLength: 8
};

export type PasswordRuleId =
  | "length"
  | "mixed-case"
  | "number"
  | "symbol"
  | "differs-from-current"
  | "differs-from-username";

export type PasswordRuleResult = {
  id: PasswordRuleId;
  label: string;
  met: boolean;
};

export type PasswordRuleContext = {
  /** Omitted when there is no current password to compare against, e.g. an admin reset. */
  currentPassword?: string;
  userName?: string;
};

/** Each requirement and whether `password` meets it, in the order they are shown. */
export const evaluatePasswordRules = (
  password: string,
  policy: PasswordPolicy = defaultPasswordPolicy,
  context: PasswordRuleContext = {}
): PasswordRuleResult[] => {
  const rules: PasswordRuleResult[] = [
    {id: "length", label: `At least ${policy.minLength} characters`, met: password.length >= policy.minLength}
  ];

  if (policy.requireMixedCase) {
    rules.push({id: "mixed-case", label: "Upper and lower case letters", met: /[a-z]/.test(password) && /[A-Z]/.test(password)});
  }
  if (policy.requireNumber) {
    rules.push({id: "number", label: "At least one number", met: /\d/.test(password)});
  }
  if (policy.requireSymbol) {
    rules.push({id: "symbol", label: "At least one symbol", met: /[^A-Za-z0-9]/.test(password)});
  }
  if (context.currentPassword !== undefined) {
    rules.push({
      id: "differs-from-current",
      label: "Different from your current password",
      met: password.length > 0 && password !== context.currentPassword
    });
  }
  if (context.userName) {
    rules.push({
      id: "differs-from-username",
      label: "Not your username",
      met: password.length > 0 && password.toLowerCase() !== context.userName.toLowerCase()
    });
  }

  return rules;
};

export type PasswordStrength = 0 | 1 | 2 | 3 | 4;

export const passwordStrengthLabels: Record<PasswordStrength, string> = {
  0: "Too short",
  1: "Weak",
  2: "Fair",
  3: "Good",
  4: "Strong"
};

/**
 * A rough, advisory strength score. Length counts for the most; variety of character classes
 * helps. It never blocks a submit -- the policy does that.
 */
export const scorePasswordStrength = (
  password: string,
  minLength: number = defaultPasswordPolicy.minLength
): PasswordStrength => {
  if (!password || password.length < minLength) {
    return 0;
  }

  // One character repeated is weak however long it is.
  if (/^(.)\1*$/.test(password)) {
    return 1;
  }

  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length;

  let score = 1;
  if (password.length >= minLength + 4) score++;
  if (password.length >= 16) score++;
  if (variety >= 3) score++;
  if (variety === 4) score++;

  return Math.min(4, score) as PasswordStrength;
};

export type ChangePasswordValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type ChangePasswordField = keyof ChangePasswordValues;

export type ChangePasswordErrors = Partial<Record<ChangePasswordField, string>>;

const ruleErrors: Record<PasswordRuleId, (policy: PasswordPolicy) => string> = {
  "length": (policy) => `Use at least ${policy.minLength} characters.`,
  "mixed-case": () => "Use both upper and lower case letters.",
  "number": () => "Include at least one number.",
  "symbol": () => "Include at least one symbol.",
  "differs-from-current": () => "Choose a password different from your current one.",
  "differs-from-username": () => "Don't use your username as your password."
};

/** Field errors for a change-password form; empty when it is ready to send. */
export const validateChangePassword = (
  values: ChangePasswordValues,
  {
    policy = defaultPasswordPolicy,
    requireCurrentPassword = true,
    userName
  }: {policy?: PasswordPolicy; requireCurrentPassword?: boolean; userName?: string} = {}
): ChangePasswordErrors => {
  const errors: ChangePasswordErrors = {};

  if (requireCurrentPassword && !values.currentPassword) {
    errors.currentPassword = "Enter your current password.";
  }

  if (!values.newPassword) {
    errors.newPassword = "Enter a new password.";
  } else {
    const unmet = evaluatePasswordRules(values.newPassword, policy, {
      currentPassword: requireCurrentPassword ? values.currentPassword : undefined,
      userName
    }).find((rule) => !rule.met);

    if (unmet) {
      errors.newPassword = ruleErrors[unmet.id](policy);
    }
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = "Re-enter the new password.";
  } else if (values.confirmPassword !== values.newPassword) {
    errors.confirmPassword = "Passwords don't match.";
  }

  return errors;
};

/**
 * Thrown from a change-password submit handler to pin a server refusal to one field -- a wrong
 * current password is the usual case -- instead of showing it as a general failure.
 */
export class ChangePasswordFieldError extends Error {
  readonly field: ChangePasswordField;

  constructor(field: ChangePasswordField, message: string) {
    super(message);
    this.name = "ChangePasswordFieldError";
    this.field = field;
  }
}

// Checked by shape as well as class: the bundle GWT loads and the host app can each carry their
// own copy of this module, and `instanceof` fails across copies.
export const isChangePasswordFieldError = (error: unknown): error is ChangePasswordFieldError =>
  error instanceof ChangePasswordFieldError || (
    typeof error === "object" &&
    error !== null &&
    (error as {name?: unknown}).name === "ChangePasswordFieldError" &&
    typeof (error as {field?: unknown}).field === "string"
  );
